/**
 * Draws thrown splash / lingering potions (game/brewing/splash.ts) as camera-facing item sprites,
 * the glass burst in the potion's colour on impact, and the lingering clouds (a ring of coloured
 * flecks that shrinks with the cloud).
 */

import * as THREE from 'three';
import type { PotionCloud, ThrownPotion } from '../game/brewing/splash';
import { I } from '../game/itemIds';

const FLECKS = 96;
const CLOUD_FLECKS = 48;

export class PotionRenderer {
  readonly group = new THREE.Group();
  private readonly mats = new Map<number, THREE.SpriteMaterial>();
  private readonly pool: THREE.Sprite[] = [];
  private readonly flecks: { s: THREE.Sprite; life: number; vx: number; vy: number; vz: number }[] = [];
  private nextFleck = 0;
  private readonly cloudSprites: THREE.Sprite[] = [];
  private readonly fleckMat = new THREE.SpriteMaterial({ color: 0xffffff, transparent: true, opacity: 0.85, depthWrite: false });

  constructor() {
    for (let i = 0; i < FLECKS; i++) {
      const s = new THREE.Sprite(this.fleckMat.clone());
      s.scale.setScalar(0.1);
      s.visible = false;
      this.group.add(s);
      this.flecks.push({ s, life: 0, vx: 0, vy: 0, vz: 0 });
    }
  }

  private matFor(item: number): THREE.SpriteMaterial {
    let m = this.mats.get(item);
    if (!m) {
      const tex = new THREE.TextureLoader().load(`mc/item/${item === I.LINGERING_POTION ? 'lingering' : 'splash'}_potion.png`);
      tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.NearestFilter; tex.colorSpace = THREE.SRGBColorSpace;
      m = new THREE.SpriteMaterial({ map: tex, transparent: true, alphaTest: 0.1 });
      this.mats.set(item, m);
    }
    return m;
  }

  /** Glass-shatter burst of coloured flecks (vanilla's spell particles). */
  shatter(x: number, y: number, z: number, color: number, lingering: boolean): void {
    const n = lingering ? 24 : 40;
    for (let i = 0; i < n; i++) {
      const p = this.flecks[this.nextFleck];
      this.nextFleck = (this.nextFleck + 1) % FLECKS;
      p.s.position.set(x, y, z);
      (p.s.material as THREE.SpriteMaterial).color.setHex(color);
      p.s.visible = true;
      p.life = 0.5 + Math.random() * 0.5;
      p.vx = (Math.random() - 0.5) * 4; p.vy = Math.random() * 3; p.vz = (Math.random() - 0.5) * 4;
    }
  }

  update(potions: readonly ThrownPotion[], clouds: readonly PotionCloud[], alpha: number, dt: number, time: number): void {
    while (this.pool.length < potions.length) {
      const s = new THREE.Sprite(this.matFor(I.SPLASH_POTION));
      s.scale.setScalar(0.3);
      this.group.add(s);
      this.pool.push(s);
    }
    for (let i = 0; i < this.pool.length; i++) {
      const s = this.pool[i], e = potions[i];
      s.visible = !!e;
      if (!e) continue;
      s.material = this.matFor(e.item);
      s.position.set(e.px + (e.x - e.px) * alpha, e.py + (e.y - e.py) * alpha + 0.15, e.pz + (e.z - e.pz) * alpha);
    }
    for (const p of this.flecks) {
      if (p.life <= 0) continue;
      p.life -= dt;
      if (p.life <= 0) { p.s.visible = false; continue; }
      p.vy -= 9 * dt;
      p.s.position.x += p.vx * dt; p.s.position.y += p.vy * dt; p.s.position.z += p.vz * dt;
    }
    // clouds: flecks drifting inside the (shrinking) radius
    const need = clouds.length * CLOUD_FLECKS;
    while (this.cloudSprites.length < need) {
      const s = new THREE.Sprite(this.fleckMat.clone());
      s.scale.setScalar(0.18);
      this.group.add(s);
      this.cloudSprites.push(s);
    }
    let k = 0;
    for (const c of clouds) {
      const r = c.baseRadius * (1 - c.age / c.life);
      for (let i = 0; i < CLOUD_FLECKS; i++, k++) {
        const s = this.cloudSprites[k];
        const a = (i / CLOUD_FLECKS) * Math.PI * 2 + time * 0.3 + i * 1.7;
        const d = r * Math.sqrt(((i * 0.618) % 1));
        s.visible = r >= 0.5;
        s.position.set(c.x + Math.cos(a) * d, c.y + 0.1 + 0.15 * Math.sin(time * 2 + i), c.z + Math.sin(a) * d);
        (s.material as THREE.SpriteMaterial).color.setHex(c.color);
      }
    }
    for (; k < this.cloudSprites.length; k++) this.cloudSprites[k].visible = false;
  }
}
